import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { GoogleLogin } from '@react-oauth/google';
import { useAuth } from '../context/AuthContext';
import { Star, Eye, MessageSquare, RotateCcw, Circle } from 'lucide-react';
import API from '../utils/api';
import Footer from '../components/Footer';

export default function Login() {
  const { loginWithGoogle, requestMagicLink } = useAuth();
  const navigate = useNavigate();
  const [banner, setBanner] = useState(null);
  const [season, setSeason] = useState(null);
  const [loginBg, setLoginBg] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showEmailForm, setShowEmailForm] = useState(false);
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);
  const [emailLoading, setEmailLoading] = useState(false);
  const [cooldown, setCooldown] = useState(0);

  useEffect(() => {
    API.get('/api/public/banner').then(r => setBanner(r.data.banner)).catch(() => {});
    API.get('/api/public/active-season').then(r => setSeason(r.data.season)).catch(() => {});
    API.get('/api/public/login-background').then(r => setLoginBg(r.data.background)).catch(() => {});
  }, []);

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const handleGoogle = async (cr) => {
    setLoading(true); setError('');
    try {
      const user = await loginWithGoogle(cr.credential);
      navigate((user.role === 'admin' || user.role === 'super_admin') ? '/admin' : user.role === 'reviewer' ? '/reviewer' : '/dashboard');
    } catch { setError('Sign-in failed. Please try again.'); }
    finally { setLoading(false); }
  };

  const handleMagicLinkRequest = async (e) => {
    e.preventDefault();
    if (!email.trim() || cooldown > 0) return;
    setEmailLoading(true); setError('');
    try {
      await requestMagicLink(email.trim());
      setEmailSent(true);
      setCooldown(30);
    } catch (err) { setError(err.response?.data?.error || 'Could not send login link. Please try again.'); }
    finally { setEmailLoading(false); }
  };

  const features = [
    { icon: <Star size={16} color="var(--pink)" />, title: 'Professional anonymous scoring', desc: 'Cinematography, Screenplay, Direction & more' },
    { icon: <Eye size={16} color="var(--pink)" />, title: 'Real-time review tracking', desc: 'Know exactly where your film stands at every stage' },
    { icon: <MessageSquare size={16} color="var(--pink)" />, title: 'Honest written feedback', desc: 'Actionable critique from our editorial team' },
    { icon: <RotateCcw size={16} color="var(--pink)" />, title: 'Resubmission allowed', desc: 'Improve based on feedback and try again' },
  ];

  const pageStyle = loginBg ? {
    ...s.page,
    position: 'relative',
    backgroundImage: `url(${loginBg.image_url})`,
    backgroundSize: 'cover',
    backgroundPosition: loginBg.focal_point,
  } : s.page;

  const overlayBg = loginBg && loginBg.overlay_style === 'gradient'
    ? `linear-gradient(to right, rgba(0,0,0,${loginBg.overlay_opacity / 100}) 0%, rgba(0,0,0,${(loginBg.overlay_opacity / 100) * 0.3}) 55%, rgba(0,0,0,0) 100%)`
    : loginBg ? `rgba(0,0,0,${loginBg.overlay_opacity / 100})` : null;

  return (
    <div style={pageStyle} data-theme="dark">
      {loginBg && (
        <div style={{ position: 'absolute', inset: 0, background: overlayBg }} />
      )}
      {/* Banner */}
      {banner && (
        <div style={{ position: 'relative', zIndex: 1, background: banner.bg_color, color: banner.text_color, padding: '10px 32px', display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, fontWeight: 600 }}>
          <span>{banner.title}</span>
          {banner.subtitle && <span style={{ opacity: 0.8, fontWeight: 400 }}> · {banner.subtitle}</span>}
          {banner.cta_text && <a href={banner.cta_link} style={{ color: banner.text_color, fontWeight: 800, textDecoration: 'none', marginLeft: 'auto' }}>{banner.cta_text} →</a>}
        </div>
      )}

      <div className="login-container" style={{ position: 'relative', zIndex: 1 }}>
        {/* Left */}
        <div style={s.left} className="fade-up">
          <div style={{ ...s.eyebrow, textShadow: loginBg ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}>Gateway</div>
          <img src="/logo.png" alt="Shortflix" style={{ ...s.heroLogo, filter: loginBg ? 'drop-shadow(0 2px 8px rgba(0,0,0,0.7))' : 'none' }}
            onError={e => { e.target.style.display = 'none'; e.target.nextElementSibling.style.display = 'block'; }} />
          <span style={{ display: 'none', ...s.heroLogoFallback, textShadow: loginBg ? '0 2px 8px rgba(0,0,0,0.7)' : 'none' }}>shortflix</span>
          <div style={{ ...s.heroTagline, textShadow: loginBg ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}>India's First Curated Film Platform</div>
          <p style={{ ...s.sub, textShadow: loginBg ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}>
            Submit your film. Track every stage of review. Receive professional anonymous critique — regardless of the outcome. We invest in creators, not just content.
          </p>

          {season ? (
            <div style={s.chip}>
              <Circle size={8} fill="var(--pink)" color="var(--pink)" style={{ animation: 'pulse 2s infinite' }} />
              <span style={{ fontWeight: 700 }}>{season.name}</span>
              {season.end_date && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, background: 'var(--pink-glow)', color: 'var(--pink)', fontWeight: 700, fontSize: 11, padding: '3px 10px', borderRadius: 12, letterSpacing: '0.02em' }}>
                  Closes {new Date(season.end_date).toLocaleDateString('en-IN', { day:'numeric', month:'short' })}
                </span>
              )}
            </div>
          ) : (
            <div style={s.chip}>
              <Circle size={8} fill="var(--green)" color="var(--green)" />
              <span style={{ fontWeight: 700 }}>Open Submissions</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 12 }}>· Accepting films year-round</span>
            </div>
          )}

          <div style={s.features}>
            {features.map(f => (
              <div key={f.title} style={s.feature}>
                <div style={s.featureIcon}>{f.icon}</div>
                <div>
                  <div style={{ ...s.featureTitle, textShadow: loginBg ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}>{f.title}</div>
                  <div style={{ ...s.featureDesc, textShadow: loginBg ? '0 1px 4px rgba(0,0,0,0.7)' : 'none' }}>{f.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Right */}
        <div className="login-right fade-up">
          <div className="card" style={s.card}>
            <h2 style={s.cardTitle}>Welcome back</h2>
            <p style={s.cardSub}>Sign in with Google to submit and track your films</p>

            {error && <div style={s.errorBox}>{error}</div>}

            <div style={{ marginBottom: 20 }}>
              {loading
                ? <div style={s.loadingBtn}>Signing in...</div>
                : <GoogleLogin onSuccess={handleGoogle} onError={() => setError('Sign-in failed.')}
                    theme="filled_black" size="large" width="100%" text="continue_with" shape="rectangular" />
              }
            </div>

            <div style={s.orRow}>
              <hr style={s.orLine} /><span style={s.orText}>or</span><hr style={s.orLine} />
            </div>

            {emailSent ? (
              <div style={s.sentBox}>
                <div style={{ fontWeight: 700, marginBottom: 4 }}>Check your inbox ✉️</div>
                <div style={{ fontSize: 12.5, color: 'var(--text-secondary)', lineHeight: 1.6 }}>
                  We've sent a login link to <strong>{email}</strong>. It expires in 15 minutes and can only be used once.
                </div>
                <div style={{ display: 'flex', gap: 14, marginTop: 10, flexWrap: 'wrap' }}>
                  <button
                    onClick={handleMagicLinkRequest}
                    disabled={cooldown > 0 || emailLoading}
                    style={{ ...s.linkBtn, marginTop: 0, opacity: cooldown > 0 ? 0.5 : 1, cursor: cooldown > 0 ? 'not-allowed' : 'pointer' }}>
                    {cooldown > 0 ? `Resend in ${cooldown}s` : emailLoading ? 'Sending...' : 'Resend link'}
                  </button>
                  <button onClick={() => { setEmailSent(false); setShowEmailForm(true); }} style={{ ...s.linkBtn, marginTop: 0, color: 'var(--text-secondary)' }}>
                    Use a different email
                  </button>
                </div>
              </div>
            ) : showEmailForm ? (
              <form onSubmit={handleMagicLinkRequest} style={{ marginBottom: 20 }}>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  style={s.emailInput}
                />
                <button type="submit" disabled={emailLoading} style={s.emailSubmitBtn}>
                  {emailLoading ? 'Sending...' : 'Send login link'}
                </button>
              </form>
            ) : (
              <button onClick={() => setShowEmailForm(true)} style={s.guidelinesBtn}>
                Continue with Email →
              </button>
            )}

            <button onClick={() => navigate('/guidelines')} style={s.guidelinesBtnOutline}>
              Read submission guidelines →
            </button>

            <p style={s.disclaimer}>
              By signing in, you confirm that you own all rights to any films you submit. Your personal contact details are visible only to the Shortflix admin team and are never shared with reviewers.
            </p>
          </div>

          <div style={s.statsRow}>
            {[['500+','Films Reviewed'],['4.2★','Avg Score'],['48h','Avg Response']].map(([v,l]) => (
              <div key={l} className="card" style={s.stat}>
                <div style={s.statVal}>{v}</div>
                <div style={s.statLabel}>{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div style={{ position: 'relative', zIndex: 1 }}>
        <Footer overImage={!!loginBg} />
      </div>
    </div>
  );
}

const s = {
  page: { minHeight: '100vh', background: 'var(--bg)', display: 'flex', flexDirection: 'column' },
  themeBtn: { width: 34, height: 34, borderRadius: 'var(--r-sm)', background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' },
  container: { flex: 1, display: 'flex', maxWidth: 1120, margin: '0 auto', width: '100%', padding: '64px 32px', gap: 80, alignItems: 'center' },
  left: { flex: 1 },
  eyebrow: { fontSize: 10, fontWeight: 800, color: 'var(--pink)', letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 18 },
  headline: { fontWeight: 900, fontSize: 54, lineHeight: 1.08, marginBottom: 20, letterSpacing: '-0.02em', color: 'var(--text)' },
  headlinePink: { color: 'var(--pink)' },
  heroLogo: { height: 76, display: 'block', marginBottom: 18 },
  heroLogoFallback: { fontFamily: 'Montserrat, sans-serif', fontWeight: 900, fontSize: 52, color: 'var(--pink)', letterSpacing: '-0.02em', marginBottom: 18 },
  heroTagline: { fontSize: 13, fontWeight: 700, color: 'var(--text-secondary)', letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: 22 },
  sub: { color: 'var(--text-secondary)', fontSize: 15, lineHeight: 1.7, marginBottom: 28, maxWidth: 420, fontWeight: 400 },
  chip: { display: 'inline-flex', alignItems: 'center', gap: 8, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 24, padding: '8px 16px', fontSize: 13, marginBottom: 36 },
  features: { display: 'flex', flexDirection: 'column', gap: 14 },
  feature: { display: 'flex', alignItems: 'flex-start', gap: 12 },
  featureIcon: { width: 36, height: 36, background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 },
  featureTitle: { fontSize: 13, fontWeight: 700, color: 'var(--text)', marginBottom: 2 },
  featureDesc: { fontSize: 12, color: 'var(--text-muted)', fontWeight: 400 },
  right: { width: 370, flexShrink: 0 },
  card: { padding: 30, marginBottom: 12 },
  cardTitle: { fontWeight: 800, fontSize: 22, color: 'var(--text)', marginBottom: 6 },
  cardSub: { color: 'var(--text-secondary)', fontSize: 13, marginBottom: 24, fontWeight: 400 },
  errorBox: { background: 'rgba(255,68,68,0.08)', border: '1.5px solid rgba(255,68,68,0.25)', color: 'var(--red)', borderRadius: 'var(--r-md)', padding: '10px 14px', fontSize: 13, marginBottom: 16, fontWeight: 500 },
  loadingBtn: { background: 'var(--bg-elevated)', color: 'var(--text-muted)', padding: 12, borderRadius: 'var(--r-md)', textAlign: 'center', fontSize: 13 },
  orRow: { display: 'flex', alignItems: 'center', gap: 10, margin: '16px 0' },
  orLine: { flex: 1, border: 'none', height: '1.5px', background: 'var(--border)' },
  orText: { fontSize: 11, color: 'var(--text-muted)', flexShrink: 0, fontWeight: 600 },
  guidelinesBtn: { display: 'block', width: '100%', textAlign: 'center', color: 'var(--pink)', fontSize: 13, fontWeight: 700, textDecoration: 'none', marginBottom: 12, padding: '11px', background: 'var(--pink-glow)', borderRadius: 'var(--r-md)', border: '1.5px solid var(--pink-glow-strong)', cursor: 'pointer', fontFamily: 'var(--font)', letterSpacing: '0.02em' },
  guidelinesBtnOutline: { display: 'block', width: '100%', textAlign: 'center', color: 'var(--text-secondary)', fontSize: 12.5, fontWeight: 700, textDecoration: 'none', marginBottom: 18, padding: '10px', background: 'transparent', borderRadius: 'var(--r-md)', border: '1.5px solid var(--border)', cursor: 'pointer', fontFamily: 'var(--font)' },
  emailInput: { width: '100%', padding: '11px 14px', fontSize: 13, borderRadius: 'var(--r-md)', border: '1.5px solid var(--border)', background: 'var(--bg-elevated)', color: 'var(--text)', marginBottom: 10, fontFamily: 'var(--font)', boxSizing: 'border-box' },
  emailSubmitBtn: { width: '100%', padding: '11px', fontSize: 13, fontWeight: 800, letterSpacing: '0.02em', color: '#fff', background: 'var(--pink)', border: 'none', borderRadius: 'var(--r-md)', cursor: 'pointer', fontFamily: 'var(--font)', marginBottom: 12 },
  sentBox: { background: 'var(--bg-elevated)', border: '1.5px solid var(--border)', borderRadius: 'var(--r-md)', padding: '16px', marginBottom: 12 },
  linkBtn: { display: 'block', marginTop: 10, background: 'none', border: 'none', color: 'var(--pink)', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)', padding: 0 },
  disclaimer: { fontSize: 11, color: 'var(--text-muted)', lineHeight: 1.6, textAlign: 'center', fontWeight: 400 },
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(3,1fr)', gap: 8 },
  stat: { padding: '14px', textAlign: 'center' },
  statVal: { fontWeight: 900, fontSize: 20, color: 'var(--pink)', marginBottom: 2 },
  statLabel: { fontSize: 10, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.06em' },
};
